import { useState } from 'react';
import { motion } from 'motion/react';
import * as Icons from 'lucide-react';
import { useSiteConfig, resolveUploadUrl } from '../utils/siteConfig';

export function Portfolio() {
  const { config } = useSiteConfig();
  const portfolio = config.portfolio;
  const [selectedProject, setSelectedProject] = useState<any | null>(null);

  return (
    <div className="pt-24 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-24 px-4 md:px-8">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="px-4 py-1.5 bg-primary/5 border border-primary/20 text-primary rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-4 shadow-sm">
            Our Work
          </span>
          <h2 className="text-4xl md:text-6xl mb-6 text-slate-900 font-black tracking-tight heading-glow">Success Stories</h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed font-sans">
            A showcasing of our craft, creativity, and results-driven strategies built for leading brands globally.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {portfolio.map((project, index) => (
            <motion.div
              key={index}
              onClick={() => setSelectedProject(project)}
              className="group cursor-pointer hover:-translate-y-1.5 transition-all duration-300 h-full flex flex-col"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="rounded-[2rem] overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                {project.image ? (
                  <div className="h-52 overflow-hidden relative">
                    <img 
                      src={resolveUploadUrl(project.image)} 
                      alt={project.title} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                    />
                  </div>
                ) : (
                  <div className="h-4 bg-slate-50 border-b border-slate-100" />
                )}
                
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    {/* Category */}
                    <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest block mb-2">
                      {project.category}
                    </span>
                    
                    {/* Title & Tags */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-black text-slate-900 leading-snug">
                        {project.title}
                      </h3>
                      
                      {project.tags && (
                        <div className="flex flex-wrap gap-1.5">
                          {project.tags.split(',').map((tag, tIdx) => (
                            <span 
                              key={tIdx} 
                              className="px-2.5 py-0.5 border border-slate-200 text-slate-500 rounded-full text-[10px] font-semibold tracking-wide"
                            >
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-slate-500 text-xs mt-3 line-clamp-2 leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {selectedProject && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/65 backdrop-blur-md"
          onClick={() => setSelectedProject(null)}
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[2.5rem] overflow-hidden shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col md:flex-row relative border border-slate-100"
          >
            {/* Back Button */}
            <button 
              onClick={() => setSelectedProject(null)}
              className="absolute top-6 left-6 z-10 px-5 py-2.5 bg-white/90 backdrop-blur-md hover:bg-slate-50 text-slate-800 rounded-full border border-slate-200/80 shadow-md hover:shadow-lg transition-all flex items-center gap-2 font-semibold text-sm cursor-pointer"
            >
              <Icons.ArrowLeft size={16} /> Back
            </button>

            {/* Left/Top: Large Image */}
            <div className="w-full md:w-3/5 h-48 sm:h-64 md:h-auto min-h-[220px] sm:min-h-[300px] md:min-h-[500px] relative bg-slate-100 shrink-0">
              {selectedProject.image ? (
                <img 
                  src={resolveUploadUrl(selectedProject.image)} 
                  alt={selectedProject.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  No Image Available
                </div>
              )}
            </div>

            {/* Right/Bottom: Content Details */}
            <div className="w-full md:w-2/5 p-8 md:p-12 overflow-y-auto flex flex-col justify-between bg-white max-h-[90vh] md:max-h-auto">
              <div className="space-y-6 pt-8 md:pt-0">
                <span className="text-xs font-bold text-emerald-500 uppercase tracking-widest block">
                  {selectedProject.category}
                </span>
                
                <h3 className="text-3xl font-black text-slate-900 leading-tight">
                  {selectedProject.title}
                </h3>
                
                {selectedProject.tags && (
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags.split(',').map((tag: string, tIdx: number) => (
                      <span 
                        key={tIdx} 
                        className="px-3.5 py-1 border border-slate-200 text-slate-600 rounded-full text-xs font-semibold"
                      >
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}
                
                <hr className="border-slate-100" />
                
                <div>
                  <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Project Overview</h4>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed font-sans">
                    {selectedProject.description}
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-100 flex gap-4">
                <button 
                  onClick={() => setSelectedProject(null)}
                  className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all text-center cursor-pointer"
                >
                  Close Project
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
